import React, { Component } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import { intlShape, injectIntl } from 'react-intl';

import MediaCard from '@redbubble/design-system/react/MediaCard';

import Favorite from '../../Favorite';
import { analyticsPayload } from '../../../lib/analytics';
import { workLocation } from '../../../lib/routing/routing';
import { UniqueWithinTypeDecode } from '../../../apollo';
import WorkDetails from '../../WorkDetails';
import cachedPlaceholderSrc from '../../../lib/cachedPlaceholderImage';
import {
  AnalyticsItemsList,
  RbGtmClient,
  parseWorkToAnalyticsItem
} from '../../../lib/gtm';
import messages from './messages';

class FeedGridItem extends Component {
  constructor(props) {
    super(props);
    this.accumulateImpression = this.accumulateImpression.bind(this);
  }

  componentDidMount() {
    if (typeof IntersectionObserver !== 'undefined') {
      this.observer = new IntersectionObserver(this.accumulateImpression);
      this.observer.observe(this.element);
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  locationWithId() {
    const { work, location, useModal, locale } = this.props;

    const workId = UniqueWithinTypeDecode(work.id);
    const modalIsOpen = location.state && location.state.modal;
    const modal = modalIsOpen || useModal;

    return workLocation({ workId, modal, locale });
  }

  accumulateImpression(entries) {
    const entry = entries[0];
    if (entry.isIntersecting) {
      const { addImpression, analyticsLabel, index, work } = this.props;
      const workId = UniqueWithinTypeDecode(work.id);
      const rank = index + 1;

      addImpression(analyticsPayload.impression(analyticsLabel, workId, rank));
      this.observer.disconnect();
    }
  }

  render() {
    const {
      work,
      analyticsLabel,
      index,
      logEvent,
      intl,
      addWorkToFavorites,
      removeWorkFromFavorites,
      isLoggedIn,
      openSignUpModal,
      setModalImageUrl,
      loadImage
    } = this.props;

    const workId = UniqueWithinTypeDecode(work.id);

    const workLinkLocation = this.locationWithId();
    const rank = index + 1;
    const onWorkClick = () =>
      logEvent({
        analytics: analyticsPayload.clickThrough(analyticsLabel, workId, rank)
      });
    const { width, height } = get(work, 'image', {});
    const imageUrl = get(work, 'image.url');

    const handleNonMemberFavorite = () => {
      setModalImageUrl(imageUrl);
      openSignUpModal();
    };

    const favorited = get(work, 'isFavorited', false);

    const toggleFavorite = () => {
      logEvent({
        analytics: analyticsPayload.favorite({
          newState: !favorited,
          workId,
          rank
        })
      });

      if (favorited) {
        removeWorkFromFavorites(workId);
      } else {
        addWorkToFavorites(workId);

        try {
          RbGtmClient.track({
            event: 'add_to_wishlist',
            ecommerce: {
              items: [
                parseWorkToAnalyticsItem({
                  work,
                  index,
                  analyticsComponentID: AnalyticsItemsList.ExploreFeed
                })
              ],
              currency: null,
              value: null
            }
          });
        } catch (error) {
          console.error(
            'There was error reporting add_to_wishlist event',
            error
          );
        }
      }
    };

    const handleFavoriteClick = isLoggedIn
      ? toggleFavorite
      : handleNonMemberFavorite;

    const loadOnVisible = { enabled: true };
    const placeholder = cachedPlaceholderSrc(width, height);

    if (placeholder) loadOnVisible.placeholder = placeholder;

    return (
      <div
        ref={element => {
          this.element = element;
        }}
      >
        <MediaCard
          element={Link}
          to={workLinkLocation}
          onClick={onWorkClick}
          rel="nofollow"
          title={get(work, 'title')}
          caption={intl.formatMessage(messages.artistAttribution, {
            artistName: get(work, 'artistShopDisplayName')
          })}
          imageSrc={loadImage ? imageUrl : placeholder}
          transparent
          loadOnVisible={loadOnVisible}
          primaryActionSlot={
            <Favorite favorited={favorited} onClick={handleFavoriteClick} />
          }
        />
      </div>
    );
  }
}

FeedGridItem.propTypes = {
  intl: intlShape.isRequired,
  work: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    artist: PropTypes.shape({
      name: PropTypes.string,
      portfolioUrl: PropTypes.string
    }),
    image: PropTypes.shape({
      url: PropTypes.string.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired
    }),
    isFavorited: PropTypes.bool
  }),
  analyticsLabel: PropTypes.string,
  useModal: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    state: PropTypes.shape({
      modal: PropTypes.bool
    })
  }),
  addWorkToFavorites: PropTypes.func.isRequired,
  removeWorkFromFavorites: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  openSignUpModal: PropTypes.func.isRequired,
  setModalImageUrl: PropTypes.func.isRequired,
  loadImage: PropTypes.bool
};

FeedGridItem.defaultProps = {
  work: {
    isFavorited: false
  },
  useModal: false,
  location: {
    pathname: ''
  },
  loadImage: true
};

FeedGridItem.displayName = 'FeedGridItem';

FeedGridItem.fragment = gql`
  fragment FeedGridItem on Work {
    id
    artistId
    title
    url
    artistShopDisplayName
    artist {
      name
      portfolioUrl(locale: $locale)
      avatar
    }
    image {
      url
      width
      height
    }
    isFavorited
    ...WorkDetails
  }
  ${WorkDetails.fragment}
`;

const FeedGridItemWithIntl = injectIntl(FeedGridItem);
FeedGridItemWithIntl.displayName = FeedGridItem.displayName;

export { FeedGridItem };
export default FeedGridItemWithIntl;
